import { Fragment } from 'react';

import { getProductHyperlink } from '@changegps/horizon-component-library';
import { Divider, Skeleton, Stack, Typography } from '@mui/material';

import DocIcon from '../assets/doc.svg?react';
import { useRecentWork } from '../hooks';
import { CoreProduct, Product, ProductData } from '../types/recent-work';

import { Card } from './Card';
import { HomeLayout } from './HomeLayout';
import { Icon } from './Icon';

export const RecentWork = () => {
  const { data: workList, isFetching } = useRecentWork();

  const getWorkTitle = (work: ProductData): string => {
    let formattedFileType = '';

    const productTypeMap: Record<Product, string | undefined> = {
      taxplan: 'TaxPlan',
      progress: 'Progress Report',
      protect: 'Protect Plan',
      engage: 'Engagement',
      core: undefined, // Core products are handled separately
      settings: undefined, // Not specified in your mapping
    };

    const subProductTypeMap: Record<CoreProduct, string | undefined> = {
      'trust-distribution': 'Trust Distribution Report',
      structure: 'Structure Advice Report',
      'div-statements': 'Dividend Statement',
      fbt: 'FBT Report',
      actions: 'Meeting',
      blueprint: 'Blueprint',
      collate: 'Collate',
      letters: 'Letter',
      'service-plan': 'Service Plan',
      'cgt-advice': 'CGT Advice',
      // The following values exist in CoreProduct but were not mapped
      meetings: undefined,
      potential: undefined,
      resources: undefined,
    };

    // Determine formattedFileType based on product_type or sub_product_type
    formattedFileType =
      productTypeMap[work.product_type] ||
      (work.product_type === 'core'
        ? (subProductTypeMap[work.sub_product_type] ?? '')
        : '');

    return (
      work.file_name + (formattedFileType ? ` - ${formattedFileType}` : '')
    );
  };

  const handleNavigation = (work: ProductData) => {
    let subProduct: CoreProduct | undefined = undefined;

    if (work.product_type === 'core') {
      // Map your local 'actions' to library's 'meetings'
      if (work.sub_product_type === 'actions') {
        subProduct = 'meetings';
      } else {
        subProduct = work.sub_product_type;
      }
    }

    window.location.href = getProductHyperlink({
      environment: ENV.ENVIRONMENT,
      product: work.product_type,
      subProduct,
    });
  };

  return (
    <HomeLayout title="Recent work">
      <Card>
        {isFetching ? (
          <LoadingSkeleton />
        ) : (
          <>
            {workList?.map((item, index: number) => (
              <div key={index}>
                {index > 0 && <Divider />}
                <Stack
                  direction="row"
                  px={2}
                  py={2.5}
                  spacing={1.5}
                  sx={{ cursor: 'pointer' }}
                  alignItems="center"
                  onClick={() => handleNavigation(item)}
                >
                  <Icon icon={<DocIcon />} color="#0075c9" />
                  <Typography fontSize={15} fontWeight={400} lineHeight={1.6}>
                    {getWorkTitle(item)}
                  </Typography>
                </Stack>
              </div>
            ))}
          </>
        )}
      </Card>
    </HomeLayout>
  );
};

const LoadingSkeleton = () => {
  return (
    <Stack spacing={1}>
      {Array.from({ length: 5 }).map((_, index) => (
        <Fragment key={'recent-work-loading-skeleton' + index}>
          {index > 0 && <Divider />}
          <Skeleton key={index} variant="rounded" width={'100%'} height={64} />
        </Fragment>
      ))}
    </Stack>
  );
};
